import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Container,
  Row,
  Col,
  Form,
  Table,
  Button,
  Modal,
  Badge,
  Spinner,
  Pagination,
  Alert
} from 'react-bootstrap';
import {
  Truck,
  RefreshCw,
  Download,
  ExternalLink,
  Package,
  Clock,
  ShieldAlert,
  MapPin,
  Timer,
  Smartphone,
  Eye,
  Settings
} from 'lucide-react';
import { apiService } from '../services/api';
import DeliveriesRouteMap from './DeliveriesRouteMap';
import {
  LOAD_STATUS_FILTER_OTHER,
  LOAD_STATUS_IN_TRANSIT_DISPLAY,
  deliveriesLoadStatusBadgeProps,
  loadStatusDisplayFromRaw
} from '../constants/logisticsDisplay';
import './Deliveries.css';

const formatDt = (v) => {
  if (!v) return '—';
  try {
    const s = typeof v === 'string' ? v.trim().replace(/^(\d{4}-\d{2}-\d{2}) (\d{2}:\d{2}:\d{2})/, '$1T$2') : v;
    return new Date(s).toLocaleString();
  } catch {
    return String(v);
  }
};

const formatDateOnly = (v) => {
  if (!v) return '—';
  try {
    const s = typeof v === 'string' ? v.trim().replace(/^(\d{4}-\d{2}-\d{2}) (\d{2}:\d{2}:\d{2})/, '$1T$2') : v;
    return new Date(s).toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  } catch {
    return String(v);
  }
};

/** Loads.DeliveryDate — show date, or date+time when the schedule includes a non-midnight time. */
const formatScheduledDeliveryField = (v) => {
  if (!v) return '—';
  try {
    const s = typeof v === 'string' ? v.trim().replace(/^(\d{4}-\d{2}-\d{2}) (\d{2}:\d{2}:\d{2})/, '$1T$2') : v;
    const d = new Date(s);
    if (Number.isNaN(d.getTime())) return String(v);
    const z = new Date(d.getFullYear(), d.getMonth(), d.getDate());
    const isMidnight =
      d.getHours() === 0 && d.getMinutes() === 0 && d.getSeconds() === 0 && d.getMilliseconds() === 0;
    if (isMidnight && d.getTime() === z.getTime()) return formatDateOnly(v);
    return formatDt(v);
  } catch {
    return String(v);
  }
};

/** Unload column: API sends decimal minutes — show h / m / s for readability. */
const formatUnloadDuration = (minutes) => {
  if (minutes == null || minutes === '') return '—';
  const n = Number(minutes);
  if (!Number.isFinite(n) || n < 0) return '—';
  const totalSeconds = Math.round(n * 60);
  const h = Math.floor(totalSeconds / 3600);
  const m = Math.floor((totalSeconds % 3600) / 60);
  const s = totalSeconds % 60;
  const parts = [];
  if (h > 0) parts.push(`${h}h`);
  if (h > 0 || m > 0) parts.push(`${m}m`);
  parts.push(`${s}s`);
  return parts.join(' ');
};

const DEFAULT_DELIVERY_PAGE_LIMIT = 25;
const DELIVERY_PAGE_LIMIT_OPTIONS = [10, 25, 50, 100];
const DEFAULT_PERF_PAGE_LIMIT = 50;
const PERF_PAGE_LIMIT_OPTIONS = [25, 50, 100, 200];

/** Page numbers for Bootstrap Pagination (inserts ellipsis where there are gaps). */
function visiblePageNumbers(current, totalPages) {
  if (totalPages <= 0) return [];
  if (totalPages <= 7) {
    return Array.from({ length: totalPages }, (_, i) => i + 1);
  }
  const delta = 2;
  const range = [];
  for (let i = 1; i <= totalPages; i += 1) {
    if (i === 1 || i === totalPages || (i >= current - delta && i <= current + delta)) {
      range.push(i);
    }
  }
  const withDots = [];
  let l;
  for (const i of range) {
    if (l != null) {
      if (i - l === 2) withDots.push(l + 1);
      else if (i - l > 1) withDots.push('ellipsis');
    }
    withDots.push(i);
    l = i;
  }
  return withDots;
}

/** Local calendar YYYY-MM-DD (avoids UTC drift from `toISOString().slice(0, 10)`). */
const localYmd = (d) => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

const perfRangeToday = () => {
  const t = new Date();
  const s = localYmd(t);
  return { fd: s, td: s };
};

const perfRangeYesterday = () => {
  const t = new Date();
  t.setDate(t.getDate() - 1);
  const s = localYmd(t);
  return { fd: s, td: s };
};

/** Matches main list "Last N days" preset: from (today − n) through today, local dates. */
const perfRangeRollingDays = (daysBack) => {
  const end = new Date();
  const start = new Date();
  start.setDate(start.getDate() - daysBack);
  return { fd: localYmd(start), td: localYmd(end) };
};

const perfRangeThisMonth = () => {
  const end = new Date();
  const start = new Date(end.getFullYear(), end.getMonth(), 1);
  return { fd: localYmd(start), td: localYmd(end) };
};

const googleMapsUrl = (lat, lng) => {
  if (lat == null || lng == null || Number.isNaN(Number(lat)) || Number.isNaN(Number(lng))) return null;
  return `https://www.google.com/maps?q=${encodeURIComponent(`${Number(lat)},${Number(lng)}`)}`;
};


const SLA_ALERT_TYPE = 'sla_overdue_in_transit';

const EVENT_TYPE_LABELS = {
  qr_validated: 'QR validated',
  pickup_attempt: 'Pickup attempt',
  pickup_outside_override_confirmed: 'Pickup confirmed (outside zone)',
  delivery_attempt: 'Delivery attempt',
  delivery_site_arrived: 'Arrived at delivery site',
  delivery_confirmed: 'Delivery confirmed',
  delivery_outside_override_confirmed: 'Delivery confirmed (outside zone)',
  geofence_enter: 'Entered job zone',
  geofence_exit: 'Left job zone',
  driver_note: 'Driver note',
  admin_manual_delivered: 'Marked delivered (admin)'
};

const DRIVER_NOTE_CATEGORY_LABELS = {
  delay: 'Delay',
  breakdown: 'Breakdown',
  driver_change: 'Driver change',
  other: 'Other'
};

const punctualityLabel = (key) => {
  const k = String(key || '').toLowerCase();
  if (k === 'on_time') return 'On time';
  if (k === 'late') return 'Late';
  if (k === 'no_schedule') return 'No schedule';
  if (k === 'unknown') return '—';
  return key ? String(key) : '—';
};

const punctualityBadgeBg = (key) => {
  const k = String(key || '').toLowerCase();
  if (k === 'on_time') return 'success';
  if (k === 'late') return 'danger';
  return 'secondary';
};

const ALERT_TYPE_LABELS = {
  location_mismatch: 'GPS outside allowed area',
  sla_overdue_in_transit: 'Past delivery window (still in transit)'
};

const humanizeAlertType = (type) => {
  if (!type) return 'Alert';
  if (ALERT_TYPE_LABELS[type]) return ALERT_TYPE_LABELS[type];
  return String(type)
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());
};

const alertTypeHint = (type) => {
  const t = String(type || '').toLowerCase();
  if (t === 'location_mismatch') {
    return 'The driver’s GPS fix was outside the allowed radius around the planned pickup or delivery point. Common causes: weak GPS, testing from another location, or an override.';
  }
  if (t === 'sla_overdue_in_transit') {
    return 'The load is still marked In Transit but the scheduled delivery time has passed. Follow up for possible delay, mis-scan, or missing delivery.';
  }
  return null;
};

const severityVariant = (sev) => {
  const s = String(sev || '').toLowerCase();
  if (s === 'high' || s === 'critical') return 'danger';
  if (s === 'medium' || s === 'moderate') return 'warning';
  if (s === 'low') return 'info';
  return 'secondary';
};

/** e.g. medium → Medium (readable in UI) */
const formatSeverityLabel = (sev) => {
  if (sev == null || String(sev).trim() === '') return '';
  const s = String(sev).trim();
  return s.charAt(0).toUpperCase() + s.slice(1).toLowerCase();
};

const humanizeEventType = (type) => {
  if (!type) return 'Event';
  if (EVENT_TYPE_LABELS[type]) return EVENT_TYPE_LABELS[type];
  return String(type)
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());
};

const POD_EVENT_TYPES = new Set(['delivery_confirmed', 'delivery_outside_override_confirmed']);

function driverNoteBlock(ev) {
  if (!ev || ev.eventType !== 'driver_note') return null;
  const meta = ev.metadata;
  if (!meta || typeof meta !== 'object') return null;
  const catKey = meta.category != null ? String(meta.category) : '';
  const catLabel = catKey ? DRIVER_NOTE_CATEGORY_LABELS[catKey] || catKey : '';
  const text = meta.note != null ? String(meta.note) : '';
  if (!text && !catLabel) return null;
  return (
    <div className="deliveries-driver-note deliveries-driver-note__body border rounded p-2 mt-2 small">
      {catLabel ? <div className="fw-semibold deliveries-driver-note__category mb-1">{catLabel}</div> : null}
      {text ? <div className="text-body text-break">{text}</div> : null}
      {meta.locationNotRecorded ? (
        <div className="text-muted mt-1 fst-italic">No GPS saved with this note</div>
      ) : null}
    </div>
  );
}

function adminManualDeliveredBlock(ev) {
  if (!ev || ev.eventType !== 'admin_manual_delivered') return null;
  const meta = ev.metadata;
  if (!meta || typeof meta !== 'object') return null;
  const email = meta.completedByEmail != null ? String(meta.completedByEmail) : '';
  const uid = meta.completedByUserId != null ? String(meta.completedByUserId) : '';
  const noteText = meta.notes != null ? String(meta.notes) : '';
  if (!email && !uid && !noteText) {
    return (
      <div className="deliveries-admin-manual-delivered border rounded border-warning bg-warning bg-opacity-10 p-2 mt-2 small">
        <div className="fw-semibold text-secondary mb-0">Recorded in Streamline — not from driver GPS handoff</div>
      </div>
    );
  }
  return (
    <div className="deliveries-admin-manual-delivered border rounded border-warning bg-warning bg-opacity-10 p-2 mt-2 small">
      <div className="fw-semibold text-secondary mb-1">Recorded in Streamline — not from driver GPS handoff</div>
      {(email || uid) && (
        <div className="text-body">
          {email ? <span>By {email}</span> : null}
          {email && uid ? <span className="text-muted"> · </span> : null}
          {uid ? <span className="text-muted">User ID {uid}</span> : null}
        </div>
      )}
      {noteText ? <div className="text-body text-break mt-1">{noteText}</div> : null}
    </div>
  );
}

function proofOfDeliveryBlock(ev) {
  if (!ev || !POD_EVENT_TYPES.has(ev.eventType)) return null;
  const meta = ev.metadata;
  if (!meta || typeof meta !== 'object') return null;
  const { receiverName, receiverCompanyName, signatureDataUrl } = meta;
  if (!receiverName && !receiverCompanyName && !signatureDataUrl) return null;
  return (
    <div className="deliveries-pod">
      <div className="deliveries-pod__title small fw-semibold text-secondary mb-2">Proof of delivery</div>
      {receiverName ? (
        <div className="small mb-1">
          <span className="text-muted">Receiver: </span>
          <span className="text-body">{receiverName}</span>
        </div>
      ) : null}
      {receiverCompanyName ? (
        <div className="small mb-2">
          <span className="text-muted">Company: </span>
          <span className="text-body">{receiverCompanyName}</span>
        </div>
      ) : null}
      {signatureDataUrl ? (
        <div className="deliveries-pod__sig-wrap">
          <span className="text-muted small d-block mb-1">Signature</span>
          <img
            src={signatureDataUrl}
            alt="Receiver signature"
            className="deliveries-pod-signature"
          />
        </div>
      ) : null}
    </div>
  );
}

/** Readable distance; avoids huge raw meter counts from bad data */
const formatDistanceMeters = (m) => {
  if (m == null || !Number.isFinite(Number(m))) return null;
  const n = Number(m);
  if (n < 0) return '—';
  if (n < 2000) return `${Math.round(n)} m`;
  if (n < 1_000_000) return `${(n / 1000).toFixed(1)} km`;
  return `${(n / 1000).toFixed(0)} km`;
};

const statusDeltaLine = (before, after) => {
  const a =
    after != null && String(after).trim() !== '' ? loadStatusDisplayFromRaw(after) : null;
  const b =
    before != null && String(before).trim() !== '' ? loadStatusDisplayFromRaw(before) : null;
  if (!a && !b) return 'No status change recorded';
  if (b && !a) return `${b} → (unchanged)`;
  if (!b && a) return `— → ${a}`;
  if (b === a) return b;
  return `${b} → ${a}`;
};

/** Repeated QR checks with no GPS clutter the list — group them in simplified view */
const isLowNoiseQr = (ev) => {
  if (!ev || ev.eventType !== 'qr_validated') return false;
  const hasGps =
    ev.latitude != null &&
    ev.longitude != null &&
    !Number.isNaN(Number(ev.latitude)) &&
    !Number.isNaN(Number(ev.longitude));
  if (hasGps) return false;
  if (ev.overrideUsed === true || ev.overrideUsed === 1) return false;
  return true;
};

const qrNoiseSignature = (ev) =>
  `${ev.statusBefore ?? ''}|${ev.statusAfter ?? ''}|${ev.overrideUsed ? 1 : 0}`;

const buildTimelineRows = (events, showFull) => {
  if (!events?.length) return [];
  if (showFull) return events.map((e) => ({ kind: 'single', key: `e-${e.id}`, event: e }));

  const rows = [];
  let i = 0;
  while (i < events.length) {
    const e = events[i];
    if (isLowNoiseQr(e)) {
      const sig = qrNoiseSignature(e);
      let count = 1;
      while (
        i + count < events.length &&
        isLowNoiseQr(events[i + count]) &&
        qrNoiseSignature(events[i + count]) === sig
      ) {
        count += 1;
      }
      const newest = events[i];
      const oldest = events[i + count - 1];
      rows.push({
        kind: 'group',
        key: `qr-g-${oldest.id}-${newest.id}`,
        count,
        newestAt: newest.occurredAt,
        oldestAt: oldest.occurredAt
      });
      i += count;
    } else {
      rows.push({ kind: 'single', key: `e-${e.id}`, event: e });
      i += 1;
    }
  }
  return rows;
};

const toLatLng = (lat, lng) => {
  const la = Number(lat);
  const ln = Number(lng);
  if (!Number.isFinite(la) || !Number.isFinite(ln)) return null;
  return { lat: la, lng: ln };
};

/**
 * Events arrive newest-first. For "actual pickup/dropoff" we want the earliest
 * confirmed/attempt GPS point so the map shows where it first happened.
 */
const pickEarliestEventWithGps = (events, eventTypes) => {
  if (!Array.isArray(events) || events.length === 0) return null;
  const types = new Set((eventTypes || []).filter(Boolean));
  for (let i = events.length - 1; i >= 0; i -= 1) {
    const e = events[i];
    if (!e) continue;
    if (types.size && !types.has(e.eventType)) continue;
    const ll = toLatLng(e.latitude, e.longitude);
    if (!ll) continue;
    return { ...e, ...ll };
  }
  return null;
};

const Deliveries = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [rows, setRows] = useState([]);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: DEFAULT_DELIVERY_PAGE_LIMIT,
    total: 0,
    totalPages: 0
  });
  const [pageLimit, setPageLimit] = useState(DEFAULT_DELIVERY_PAGE_LIMIT);

  const [bolNo, setBolNo] = useState('');
  const [status, setStatus] = useState('');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [projectId, setProjectId] = useState('');
  const [dateBasis, setDateBasis] = useState('lastEvent');
  const [openAlertsOnly, setOpenAlertsOnly] = useState(false);
  /** When true, list/export also includes In Transit + SLA-overdue loads that have no Logistics_Event yet. */
  const [includeSlaWithoutTracking, setIncludeSlaWithoutTracking] = useState(false);

  const [detailShow, setDetailShow] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detail, setDetail] = useState(null);
  const [exporting, setExporting] = useState(false);
  const [perfExpanded, setPerfExpanded] = useState(false);
  const [perfLoading, setPerfLoading] = useState(false);
  const [perfError, setPerfError] = useState(null);
  const [perfRows, setPerfRows] = useState([]);
  const [perfSummary, setPerfSummary] = useState(null);
  const [perfPagination, setPerfPagination] = useState({
    page: 1,
    limit: DEFAULT_PERF_PAGE_LIMIT,
    total: 0,
    totalPages: 0
  });
  const [perfPageLimit, setPerfPageLimit] = useState(DEFAULT_PERF_PAGE_LIMIT);
  const [perfExporting, setPerfExporting] = useState(false);
  const [driverEarlyMarginMinutes, setDriverEarlyMarginMinutes] = useState('');
  const [driverSettingsLoaded, setDriverSettingsLoaded] = useState(false);
  const [driverSettingsSaving, setDriverSettingsSaving] = useState(false);
  const [driverSettingsError, setDriverSettingsError] = useState(null);
  const [driverSettingsVisible, setDriverSettingsVisible] = useState(false);
  const [perfDetailShow, setPerfDetailShow] = useState(false);
  const [perfDetailRow, setPerfDetailRow] = useState(null);
  const [slaResolveModal, setSlaResolveModal] = useState(null);
  const [slaModalNotes, setSlaModalNotes] = useState('');
  const [slaModalSubmitting, setSlaModalSubmitting] = useState(false);
  const [perfFromDate, setPerfFromDate] = useState('');
  const [perfToDate, setPerfToDate] = useState('');
  const [perfActivePreset, setPerfActivePreset] = useState(null);
  const detailMapRef = useRef(null);

  const scrollToDetailMap = useCallback(() => {
    detailMapRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  }, []);

  const loadDeliveries = useCallback(
    async (page = 1, opts = {}) => {
      setLoading(true);
      setError(null);
      const effFrom = 'fromDate' in opts ? opts.fromDate : fromDate;
      const effTo = 'toDate' in opts ? opts.toDate : toDate;
      const effBol = 'bolNo' in opts ? opts.bolNo : bolNo;
      const effStatus = 'status' in opts ? opts.status : status;
      const effOpenAlerts = 'openAlertsOnly' in opts ? opts.openAlertsOnly : openAlertsOnly;
      const effIncludeSlaNoTrack =
        'includeSlaWithoutTracking' in opts ? opts.includeSlaWithoutTracking : includeSlaWithoutTracking;
      const effProject = 'projectId' in opts ? opts.projectId : projectId;
      const effDateBasis = 'dateBasis' in opts ? opts.dateBasis : dateBasis;
      const effLimit = 'limit' in opts ? opts.limit : pageLimit;
      const projTrim = String(effProject ?? '').trim();
      try {
        const res = await apiService.getLogisticsDeliveries({
          page,
          limit: effLimit,
          bolNo: String(effBol ?? '').trim() || undefined,
          status: effStatus || undefined,
          fromDate: effFrom || undefined,
          toDate: effTo || undefined,
          openAlertsOnly: effOpenAlerts ? 'true' : undefined,
          includeSlaWithoutTracking: effIncludeSlaNoTrack ? 'true' : undefined,
          projectId: projTrim || undefined,
          dateBasis: effDateBasis === 'scheduledDelivery' ? 'scheduledDelivery' : undefined
        });
        setRows(res.data || []);
        if (res.pagination) setPagination(res.pagination);
      } catch (e) {
        setError(e.message || 'Failed to load deliveries');
        setRows([]);
      } finally {
        setLoading(false);
      }
    },
    [bolNo, status, fromDate, toDate, openAlertsOnly, includeSlaWithoutTracking, projectId, dateBasis, pageLimit]
  );

  const applyDatePreset = (days) => {
    const end = new Date();
    const start = new Date();
    start.setDate(start.getDate() - days);
    const fd = localYmd(start);
    const td = localYmd(end);
    setFromDate(fd);
    setToDate(td);
    loadDeliveries(1, { fromDate: fd, toDate: td });
  };

  const clearAllFilters = () => {
    setBolNo('');
    setStatus('');
    setFromDate('');
    setToDate('');
    setProjectId('');
    setDateBasis('lastEvent');
    setOpenAlertsOnly(false);
    setIncludeSlaWithoutTracking(false);
    setPerfFromDate('');
    setPerfToDate('');
    setPerfActivePreset(null);
    loadDeliveries(1, {
      fromDate: '',
      toDate: '',
      bolNo: '',
      status: '',
      openAlertsOnly: false,
      includeSlaWithoutTracking: false,
      projectId: '',
      dateBasis: 'lastEvent'
    });
    if (perfExpanded) loadPerformanceReport(1, { fromDate: '', toDate: '' });
  };

  useEffect(() => {
    loadDeliveries(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await apiService.getLogisticsAdminConfig();
        if (cancelled) return;
        if (res.data?.earlyArrivalMarginMinutes != null) {
          setDriverEarlyMarginMinutes(String(res.data.earlyArrivalMarginMinutes));
        }
      } catch (e) {
        if (!cancelled) setDriverSettingsError(e.message || 'Failed to load driver settings');
      } finally {
        if (!cancelled) setDriverSettingsLoaded(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const saveDriverEarlyMarginMinutes = async () => {
    const n = parseInt(String(driverEarlyMarginMinutes).trim(), 10);
    if (!Number.isFinite(n) || n < 1 || n > 1440) {
      setDriverSettingsError('Enter a whole number between 1 and 1440 (minutes).');
      return;
    }
    setDriverSettingsSaving(true);
    setDriverSettingsError(null);
    try {
      const res = await apiService.putLogisticsAdminConfig({ earlyArrivalMarginMinutes: n });
      if (res.data?.earlyArrivalMarginMinutes != null) {
        setDriverEarlyMarginMinutes(String(res.data.earlyArrivalMarginMinutes));
      }
    } catch (e) {
      setDriverSettingsError(e.message || 'Save failed');
    } finally {
      setDriverSettingsSaving(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    loadDeliveries(1);
    if (perfExpanded) loadPerformanceReport(1);
  };

  const openDetail = async (loadId) => {
    setDetailShow(true);
    setDetailLoading(true);
    setDetail(null);
    try {
      const res = await apiService.getLogisticsDeliveryDetail(loadId);
      setDetail(res.data);
    } catch (e) {
      setDetail({ error: e.message });
    } finally {
      setDetailLoading(false);
    }
  };

  const handleExportCsv = async () => {
    setExporting(true);
    try {
      await apiService.exportLogisticsDeliveriesCsv({
        bolNo: bolNo.trim() || undefined,
        status: status || undefined,
        fromDate: fromDate || undefined,
        toDate: toDate || undefined,
        openAlertsOnly: openAlertsOnly ? 'true' : undefined,
        includeSlaWithoutTracking: includeSlaWithoutTracking ? 'true' : undefined,
        projectId: projectId.trim() || undefined,
        dateBasis: dateBasis === 'scheduledDelivery' ? 'scheduledDelivery' : undefined
      });
    } catch (e) {
      alert(e.message || 'Export failed');
    } finally {
      setExporting(false);
    }
  };

  const loadPerformanceReport = useCallback(
    async (page = 1, opts = {}) => {
      setPerfLoading(true);
      setPerfError(null);
      const effFrom = 'fromDate' in opts ? opts.fromDate : perfFromDate;
      const effTo = 'toDate' in opts ? opts.toDate : perfToDate;
      const effBol = 'bolNo' in opts ? opts.bolNo : bolNo;
      const effProject = 'projectId' in opts ? opts.projectId : projectId;
      const effDateBasis = 'dateBasis' in opts ? opts.dateBasis : dateBasis;
      const effLimit = 'limit' in opts ? opts.limit : perfPageLimit;
      const projTrim = String(effProject ?? '').trim();
      try {
        const res = await apiService.getLogisticsDeliveryPerformance({
          page,
          limit: effLimit,
          bolNo: String(effBol ?? '').trim() || undefined,
          fromDate: effFrom || undefined,
          toDate: effTo || undefined,
          projectId: projTrim || undefined,
          dateBasis: effDateBasis === 'scheduledDelivery' ? 'scheduledDelivery' : undefined
        });
        setPerfRows(res.data || []);
        setPerfSummary(res.summary || null);
        if (res.pagination) setPerfPagination(res.pagination);
      } catch (e) {
        setPerfError(e.message || 'Failed to load performance report');
        setPerfRows([]);
        setPerfSummary(null);
      } finally {
        setPerfLoading(false);
      }
    },
    [bolNo, perfFromDate, perfToDate, projectId, dateBasis, perfPageLimit]
  );

  const applyPerfDatePreset = (preset, fd, td) => {
    setPerfFromDate(fd);
    setPerfToDate(td);
    setPerfActivePreset(preset);
    loadPerformanceReport(1, { fromDate: fd, toDate: td });
  };

  const applyPerfCustomRange = (e) => {
    e?.preventDefault?.();
    let fd = perfFromDate.trim();
    let td = perfToDate.trim();
    if (fd && td && fd > td) {
      const swap = fd;
      fd = td;
      td = swap;
      setPerfFromDate(fd);
      setPerfToDate(td);
    }
    setPerfActivePreset('custom');
    loadPerformanceReport(1, { fromDate: fd || undefined, toDate: td || undefined });
  };

  const resetPerfDateRange = () => {
    const { fd, td } = perfRangeRollingDays(7);
    setPerfFromDate(fd);
    setPerfToDate(td);
    setPerfActivePreset('last7');
    loadPerformanceReport(1, { fromDate: fd, toDate: td });
  };

  /** Clear handoff date range only (all-time for BOL/project filters above). */
  const clearPerfFilters = () => {
    setPerfFromDate('');
    setPerfToDate('');
    setPerfActivePreset(null);
    loadPerformanceReport(1, { fromDate: '', toDate: '' });
  };

  const handlePerfExportCsv = async () => {
    setPerfExporting(true);
    try {
      await apiService.exportLogisticsDeliveryPerformanceCsv({
        bolNo: bolNo.trim() || undefined,
        fromDate: perfFromDate || undefined,
        toDate: perfToDate || undefined,
        projectId: projectId.trim() || undefined,
        dateBasis: dateBasis === 'scheduledDelivery' ? 'scheduledDelivery' : undefined
      });
    } catch (e) {
      alert(e.message || 'Export failed');
    } finally {
      setPerfExporting(false);
    }
  };

  const togglePerfPanel = () => {
    if (perfExpanded) {
      setPerfExpanded(false);
      return;
    }
    setPerfExpanded(true);
    queueMicrotask(() => {
      let fd = perfFromDate;
      let td = perfToDate;
      if (!fd && !td) {
        const r = perfRangeRollingDays(7);
        fd = r.fd;
        td = r.td;
        setPerfFromDate(fd);
        setPerfToDate(td);
        setPerfActivePreset('last7');
      }
      loadPerformanceReport(1, { fromDate: fd || undefined, toDate: td || undefined });
    });
  };

  const openPerfRowDetail = (row) => {
    setPerfDetailRow(row);
    setPerfDetailShow(true);
  };

  const closePerfRowDetail = () => {
    setPerfDetailShow(false);
    setPerfDetailRow(null);
  };

  const openFullDeliveryFromPerf = () => {
    if (perfDetailRow?.loadId == null) return;
    closePerfRowDetail();
    openDetail(perfDetailRow.loadId);
  };

  const resolveAlert = async (alertId) => {
    try {
      await apiService.resolveLogisticsAlert(alertId, 'Resolved from Deliveries');
      loadDeliveries(pagination.page);
      if (detail?.load?.loadId) openDetail(detail.load.loadId);
    } catch (e) {
      alert(e.message || 'Failed to resolve alert');
    }
  };

  const closeSlaResolveModal = () => {
    if (slaModalSubmitting) return;
    setSlaResolveModal(null);
    setSlaModalNotes('');
  };

  const submitSlaResolveAlertOnly = async () => {
    if (!slaResolveModal) return;
    const { alertId, loadId } = slaResolveModal;
    setSlaModalSubmitting(true);
    try {
      const note = slaModalNotes.trim();
      await apiService.resolveLogisticsAlert(alertId, note || 'Resolved from Deliveries');
      setSlaResolveModal(null);
      setSlaModalNotes('');
      loadDeliveries(pagination.page);
      await openDetail(loadId);
    } catch (e) {
      alert(e.message || 'Failed to resolve alert');
    } finally {
      setSlaModalSubmitting(false);
    }
  };

  const submitSlaManualDelivered = async () => {
    if (!slaResolveModal) return;
    const { loadId } = slaResolveModal;
    setSlaModalSubmitting(true);
    try {
      const note = slaModalNotes.trim();
      await apiService.postLogisticsManualComplete(loadId, note ? { notes: note } : {});
      setSlaResolveModal(null);
      setSlaModalNotes('');
      loadDeliveries(pagination.page);
      await openDetail(loadId);
    } catch (e) {
      alert(e.message || 'Failed to mark load delivered');
    } finally {
      setSlaModalSubmitting(false);
    }
  };

  const load = detail?.load;
  const projLine = load ? [load.projectAddress].filter(Boolean).join(' ') : '';

  const timelineRows = useMemo(
    () => buildTimelineRows(detail?.events || [], false),
    [detail?.events]
  );

  const hasAdminManualDelivered = useMemo(
    () => (detail?.events || []).some((e) => e.eventType === 'admin_manual_delivered'),
    [detail?.events]
  );

  const routeMap = detail?.routeMap;

  const actualPickupEvent = useMemo(
    () =>
      pickEarliestEventWithGps(detail?.events, [
        'pickup_outside_override_confirmed',
        'pickup_attempt'
      ]),
    [detail?.events]
  );

  const actualDropoffEvent = useMemo(
    () =>
      pickEarliestEventWithGps(detail?.events, [
        'delivery_outside_override_confirmed',
        'delivery_confirmed',
        'delivery_attempt'
      ]),
    [detail?.events]
  );

  const actualPickupPin = actualPickupEvent
    ? {
        lat: actualPickupEvent.lat,
        lng: actualPickupEvent.lng,
        label: `${humanizeEventType(actualPickupEvent.eventType)} · ${formatDt(actualPickupEvent.occurredAt)}`
      }
    : null;

  const actualDeliveryPin = actualDropoffEvent
    ? {
        lat: actualDropoffEvent.lat,
        lng: actualDropoffEvent.lng,
        label: `${humanizeEventType(actualDropoffEvent.eventType)} · ${formatDt(actualDropoffEvent.occurredAt)}`
      }
    : null;

  /** Same string as BOL/QR "Drop site:" line (ProjectSites annotation + description). */
  const bolStyleDropSiteLine =
    routeMap?.delivery?.label != null && String(routeMap.delivery.label).trim()
      ? String(routeMap.delivery.label).trim()
      : '';
  const displayAddressHeadingLine = bolStyleDropSiteLine || projLine;

  const showDetailMap =
    (routeMap && ((routeMap.pickup && routeMap.pickup.length > 0) || routeMap.delivery)) ||
    !!actualPickupPin ||
    !!actualDeliveryPin;

  return (
    <Container fluid className="deliveries-page px-3 px-md-4">
      <div className="deliveries-page__head">
        <div>
          <h1 className="deliveries-page__title">
            <Truck size={26} strokeWidth={2} />
            Deliveries
          </h1>
        </div>
        <div className="deliveries-page__actions">
          <Button
            variant="outline-secondary"
            size="sm"
            className="deliveries-btn--outline"
            onClick={() => loadDeliveries(pagination.page)}
            disabled={loading}
          >
            <RefreshCw size={16} className="me-1" />
            Refresh
          </Button>
          <Button
            type="button"
            variant="outline-secondary"
            size="sm"
            className="deliveries-btn--outline"
            active={driverSettingsVisible}
            aria-expanded={driverSettingsVisible}
            aria-controls="deliveries-driver-settings"
            onClick={() => setDriverSettingsVisible((v) => !v)}
          >
            <Settings size={16} className="me-1" aria-hidden />
            Driver app
          </Button>
          <Button
            variant="outline-secondary"
            size="sm"
            className="deliveries-btn--outline"
            onClick={togglePerfPanel}
            active={perfExpanded}
          >
            <Timer size={16} className="me-1" />
            Performance
          </Button>
          <Button
            variant="primary"
            size="sm"
            onClick={handleExportCsv}
            disabled={exporting || loading}
          >
            <Download size={16} className="me-1" />
            {exporting ? 'Exporting…' : 'Export CSV'}
          </Button>
        </div>
      </div>

      {driverSettingsVisible && (
      <div
        id="deliveries-driver-settings"
        className="deliveries-page__driver-settings mb-3 p-3 border rounded bg-light"
      >
        <h2 className="h6 d-flex align-items-center gap-2 mb-2">
          <Settings size={18} strokeWidth={2} aria-hidden />
          Driver app
        </h2>
        <p className="small text-muted mb-2 mb-md-3">
          If a driver taps I&apos;ve Arrived before the scheduled window, Streamline warns them when they are more than
          this many minutes early. They can still continue. Tyler can change this anytime; new values apply on the next
          track page load.
        </p>
        <Row className="g-2 align-items-end">
          <Col xs={12} sm={6} md={4}>
            <Form.Label className="small text-muted mb-1">Early arrival warning (minutes)</Form.Label>
            <Form.Control
              type="number"
              min={1}
              max={1440}
              inputMode="numeric"
              value={driverEarlyMarginMinutes}
              onChange={(e) => setDriverEarlyMarginMinutes(e.target.value)}
              disabled={!driverSettingsLoaded || driverSettingsSaving}
              aria-label="Minutes before scheduled time when early arrival warning applies"
            />
          </Col>
          <Col xs="auto">
            <Button
              variant="primary"
              size="sm"
              type="button"
              onClick={saveDriverEarlyMarginMinutes}
              disabled={driverSettingsSaving || !driverSettingsLoaded}
            >
              {driverSettingsSaving ? 'Saving…' : 'Save'}
            </Button>
          </Col>
        </Row>
        {driverSettingsError && (
          <Alert variant="danger" className="mt-2 mb-0 py-2 small">
            {driverSettingsError}
          </Alert>
        )}
      </div>
      )}

      <div className="deliveries-filters">
        <Form onSubmit={handleSearch}>
          <Row className="g-2 g-md-3 align-items-end">
            <Col xs={12} sm={6} md={3} lg={2}>
              <Form.Label className="small text-muted mb-1">Project (job) ID</Form.Label>
              <Form.Control
                inputMode="numeric"
                placeholder="e.g. 12345"
                value={projectId}
                onChange={(e) => setProjectId(e.target.value)}
              />
            </Col>
            <Col xs={12} sm={6} md={3} lg={2}>
              <Form.Label className="small text-muted mb-1">BOL</Form.Label>
              <Form.Control placeholder="Search BOL" value={bolNo} onChange={(e) => setBolNo(e.target.value)} />
            </Col>
            <Col xs={12} sm={6} md={4} lg={2}>
              <Form.Label className="small text-muted mb-1">Date range applies to</Form.Label>
              <Form.Select value={dateBasis} onChange={(e) => setDateBasis(e.target.value)}>
                <option value="lastEvent">Last logistics event</option>
                <option value="scheduledDelivery">Scheduled delivery date</option>
              </Form.Select>
            </Col>
            <Col xs={12} sm={6} md={3} lg={2}>
              <Form.Label className="small text-muted mb-1">Load status</Form.Label>
              <Form.Select value={status} onChange={(e) => setStatus(e.target.value)}>
                <option value="">Any</option>
                <option value={LOAD_STATUS_FILTER_OTHER}>{LOAD_STATUS_FILTER_OTHER}</option>
                <option value={LOAD_STATUS_IN_TRANSIT_DISPLAY}>{LOAD_STATUS_IN_TRANSIT_DISPLAY}</option>
                <option value="Delivered">Delivered</option>
              </Form.Select>
            </Col>
            <Col xs={6} sm={6} md={2} lg={2}>
              <Form.Label className="small text-muted mb-1">From</Form.Label>
              <Form.Control type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)} />
            </Col>
            <Col xs={6} sm={6} md={2} lg={2}>
              <Form.Label className="small text-muted mb-1">To</Form.Label>
              <Form.Control type="date" value={toDate} onChange={(e) => setToDate(e.target.value)} />
            </Col>
            <Col xs={12} sm={6} md={2} lg={2}>
              <Form.Check
                type="checkbox"
                id="openAlertsOnly"
                className="mb-1"
                label="Open alerts only"
                checked={openAlertsOnly}
                onChange={(e) => setOpenAlertsOnly(e.target.checked)}
              />
            </Col>
            <Col xs={12} sm={6} md={4} lg={3}>
              <Form.Check
                type="checkbox"
                id="includeSlaWithoutTracking"
                className="mb-1"
                label="Include SLA overdue"
                checked={includeSlaWithoutTracking}
                onChange={(e) => setIncludeSlaWithoutTracking(e.target.checked)}
              />
            </Col>
            <Col xs={12} sm={6} md={1} lg={1}>
              <Button type="submit" variant="primary" className="w-100">
                Apply
              </Button>
            </Col>
            <Col xs={12}>
              <div className="d-flex flex-wrap align-items-center gap-2 pt-1">
                <span className="small text-muted">Presets:</span>
                <Button
                  type="button"
                  size="sm"
                  variant="outline-secondary"
                  className="deliveries-btn--outline"
                  onClick={() => applyDatePreset(7)}
                >
                  Last 7 days
                </Button>
                <Button
                  type="button"
                  size="sm"
                  variant="outline-secondary"
                  className="deliveries-btn--outline"
                  onClick={() => applyDatePreset(30)}
                >
                  Last 30 days
                </Button>
                <Button
                  type="button"
                  size="sm"
                  variant="link"
                  className="text-decoration-none p-0 small deliveries-btn--link"
                  onClick={clearAllFilters}
                >
                  Clear filter
                </Button>
              </div>
            </Col>
          </Row>
        </Form>
      </div>

      {perfExpanded && (
        <div className="deliveries-filters deliveries-performance mb-3">
          <div className="d-flex flex-wrap align-items-center justify-content-between gap-2 mb-2">
            <div className="d-flex align-items-center gap-2 flex-wrap">
              <Timer size={20} className="text-body-secondary" />
              <strong>Delivery performance</strong>
              <span className="small text-muted">
                Completed handoff loads only · BOL, project, and &quot;date range applies to&quot; follow the filters
                above; the handoff range below is for this report only.
              </span>
            </div>
            <div className="d-flex flex-wrap gap-2">
              <Button
                size="sm"
                variant="outline-secondary"
                className="deliveries-btn--outline"
                onClick={() => loadPerformanceReport(perfPagination.page)}
                disabled={perfLoading}
              >
                <RefreshCw size={14} className="me-1" />
                Refresh
              </Button>
              <Button
                size="sm"
                variant="primary"
                onClick={handlePerfExportCsv}
                disabled={perfExporting}
              >
                <Download size={14} className="me-1" />
                {perfExporting ? 'Exporting…' : 'Export performance CSV'}
              </Button>
            </div>
          </div>

          <div className="deliveries-performance__date-filters border-top pt-3 mt-2">
            <div className="small text-muted mb-2">Handoff date range</div>
            <div className="d-flex flex-wrap align-items-center gap-2 mb-3">
              <Button
                type="button"
                size="sm"
                variant="outline-secondary"
                className="deliveries-btn--outline"
                active={perfActivePreset === 'today'}
                disabled={perfLoading}
                onClick={() => {
                  const { fd, td } = perfRangeToday();
                  applyPerfDatePreset('today', fd, td);
                }}
              >
                Today
              </Button>
              <Button
                type="button"
                size="sm"
                variant="outline-secondary"
                className="deliveries-btn--outline"
                active={perfActivePreset === 'yesterday'}
                disabled={perfLoading}
                onClick={() => {
                  const { fd, td } = perfRangeYesterday();
                  applyPerfDatePreset('yesterday', fd, td);
                }}
              >
                Yesterday
              </Button>
              <Button
                type="button"
                size="sm"
                variant="outline-secondary"
                className="deliveries-btn--outline"
                active={perfActivePreset === 'last7'}
                disabled={perfLoading}
                onClick={() => {
                  const { fd, td } = perfRangeRollingDays(7);
                  applyPerfDatePreset('last7', fd, td);
                }}
              >
                Last 7 days
              </Button>
              <Button
                type="button"
                size="sm"
                variant="outline-secondary"
                className="deliveries-btn--outline"
                active={perfActivePreset === 'month'}
                disabled={perfLoading}
                onClick={() => {
                  const { fd, td } = perfRangeThisMonth();
                  applyPerfDatePreset('month', fd, td);
                }}
              >
                This month
              </Button>
              <Button
                type="button"
                size="sm"
                variant="link"
                className="text-decoration-none p-0 small deliveries-btn--link align-self-center ms-1"
                disabled={perfLoading}
                onClick={clearPerfFilters}
              >
                Clear filter
              </Button>
            </div>
            <Form className="d-flex flex-wrap align-items-end gap-2" onSubmit={applyPerfCustomRange}>
              <div>
                <Form.Label className="small text-muted mb-1">Custom from</Form.Label>
                <Form.Control
                  type="date"
                  value={perfFromDate}
                  disabled={perfLoading}
                  onChange={(e) => {
                    setPerfFromDate(e.target.value);
                    setPerfActivePreset('custom');
                  }}
                />
              </div>
              <div>
                <Form.Label className="small text-muted mb-1">Custom to</Form.Label>
                <Form.Control
                  type="date"
                  value={perfToDate}
                  disabled={perfLoading}
                  onChange={(e) => {
                    setPerfToDate(e.target.value);
                    setPerfActivePreset('custom');
                  }}
                />
              </div>
              <Button
                type="submit"
                size="sm"
                variant="outline-primary"
                className="deliveries-btn--outline-primary"
                disabled={perfLoading}
              >
                Apply range
              </Button>
              <Button
                type="button"
                size="sm"
                variant="outline-secondary"
                className="deliveries-btn--outline"
                disabled={perfLoading}
                onClick={resetPerfDateRange}
              >
                Reset (last 7 days)
              </Button>
            </Form>
          </div>

          {perfError && <Alert variant="danger">{perfError}</Alert>}
          {perfSummary && (
            <div className="deliveries-perf-summary mb-3">
              <p className="deliveries-perf-summary__intro mb-2">
                These figures apply to <strong>this page</strong> of the table only — not the full filtered list. Use
                pagination or export CSV to review more loads.
              </p>
              <div className="deliveries-perf-summary__grid">
                <div className="deliveries-perf-card">
                  <div className="deliveries-perf-card__label">Avg. unload time</div>
                  <div className="deliveries-perf-card__value">
                    {formatUnloadDuration(perfSummary.avgUnloadingMinutesThisPage)}
                  </div>
                  <div className="deliveries-perf-card__hint">Site arrival → handoff</div>
                </div>
                <div className="deliveries-perf-card deliveries-perf-card--success">
                  <div className="deliveries-perf-card__label">Handoff on time</div>
                  <div className="deliveries-perf-card__value">{perfSummary.pageCompletionOnTime}</div>
                  <div className="deliveries-perf-card__hint">Completed by deadline</div>
                </div>
                <div className="deliveries-perf-card deliveries-perf-card--danger">
                  <div className="deliveries-perf-card__label">Handoff late</div>
                  <div className="deliveries-perf-card__value">{perfSummary.pageCompletionLate}</div>
                  <div className="deliveries-perf-card__hint">After scheduled deadline</div>
                </div>
                <div className="deliveries-perf-card deliveries-perf-card--neutral">
                  <div className="deliveries-perf-card__label">No schedule</div>
                  <div className="deliveries-perf-card__value">{perfSummary.pageCompletionNoSchedule}</div>
                  <div className="deliveries-perf-card__hint">Missing delivery date</div>
                </div>
                <div className="deliveries-perf-card">
                  <div className="deliveries-perf-card__label">Site arrival on time</div>
                  <div className="deliveries-perf-card__value">{perfSummary.pageArrivalOnTime ?? '—'}</div>
                  <div className="deliveries-perf-card__hint">Truck arrived by deadline</div>
                </div>
                <div className="deliveries-perf-card deliveries-perf-card--warn">
                  <div className="deliveries-perf-card__label">Site arrival late</div>
                  <div className="deliveries-perf-card__value">{perfSummary.pageArrivalLate ?? '—'}</div>
                  <div className="deliveries-perf-card__hint">Truck after deadline</div>
                </div>
              </div>
            </div>
          )}
          {perfLoading ? (
            <div className="text-center py-3">
              <Spinner animation="border" size="sm" />
            </div>
          ) : (
            <div className="table-responsive">
              <Table hover size="sm" className="mb-0 align-middle deliveries-perf-table">
                <thead>
                  <tr>
                    <th>Proj</th>
                    <th>BOL</th>
                    <th>Load #</th>
                    <th>Project</th>
                    <th className="text-nowrap">Unload</th>
                    <th>Handoff</th>
                    <th className="text-end">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {perfRows.length === 0 ? (
                    <tr>
                      <td colSpan={7} className="text-muted text-center py-3">
                        No completed deliveries in this range.
                      </td>
                    </tr>
                  ) : (
                    perfRows.map((r) => (
                      <tr key={r.loadId}>
                        <td className="small text-muted">{r.projectId ?? '—'}</td>
                        <td className="fw-semibold">{r.bolNo || '—'}</td>
                        <td>{r.loadNo || '—'}</td>
                        <td
                          className="text-truncate small"
                          style={{ maxWidth: 200 }}
                          title={r.custProjName || r.projectName}
                        >
                          {r.custProjName || r.projectName || '—'}
                        </td>
                        <td className="text-nowrap small">
                          {formatUnloadDuration(r.unloadingMinutes)}
                        </td>
                        <td>
                          <Badge bg={punctualityBadgeBg(r.completionVsSchedule)}>
                            {punctualityLabel(r.completionVsSchedule)}
                          </Badge>
                        </td>
                        <td className="text-end">
                          <Button
                            size="sm"
                            variant="outline-primary"
                            className="deliveries-btn--outline-primary"
                            onClick={() => openPerfRowDetail(r)}
                          >
                            <Eye size={14} className="me-1" aria-hidden />
                            Details
                          </Button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </Table>
            </div>
          )}
          {!perfLoading && perfPagination.total > 0 && (
            <div className="deliveries-pagination-bar mt-2">
              <div className="deliveries-pagination-bar__meta">
                <span className="muted">
                  Showing{' '}
                  {Math.min(
                    (perfPagination.page - 1) * (perfPagination.limit || perfPageLimit) + 1,
                    perfPagination.total
                  )}
                  –
                  {Math.min(
                    perfPagination.page * (perfPagination.limit || perfPageLimit),
                    perfPagination.total
                  )}{' '}
                  of {perfPagination.total} loads (this page)
                </span>
                <div className="deliveries-pagination-bar__perpage">
                  <Form.Label htmlFor="perf-page-size" className="muted mb-0 small text-nowrap">
                    Per page
                  </Form.Label>
                  <Form.Select
                    id="perf-page-size"
                    size="sm"
                    className="deliveries-pagination-bar__select"
                    value={String(perfPageLimit)}
                    disabled={perfLoading}
                    onChange={(e) => {
                      const next = Number(e.target.value);
                      setPerfPageLimit(next);
                      loadPerformanceReport(1, { limit: next });
                    }}
                    aria-label="Performance table rows per page"
                  >
                    {PERF_PAGE_LIMIT_OPTIONS.map((n) => (
                      <option key={n} value={String(n)}>
                        {n}
                      </option>
                    ))}
                  </Form.Select>
                </div>
              </div>
              <Pagination className="mb-0 flex-wrap">
                <Pagination.Prev
                  disabled={perfPagination.page <= 1 || perfLoading}
                  onClick={() => loadPerformanceReport(perfPagination.page - 1)}
                  aria-label="Previous page"
                />
                {visiblePageNumbers(perfPagination.page, perfPagination.totalPages).map((item, idx) =>
                  item === 'ellipsis' ? (
                    <Pagination.Ellipsis key={`perf-pe-${idx}`} disabled />
                  ) : (
                    <Pagination.Item
                      key={item}
                      active={item === perfPagination.page}
                      onClick={() =>
                        item !== perfPagination.page ? loadPerformanceReport(item) : undefined
                      }
                    >
                      {item}
                    </Pagination.Item>
                  )
                )}
                <Pagination.Next
                  disabled={perfPagination.page >= perfPagination.totalPages || perfLoading}
                  onClick={() => loadPerformanceReport(perfPagination.page + 1)}
                  aria-label="Next page"
                />
              </Pagination>
            </div>
          )}
        </div>
      )}

      {error && <Alert variant="danger">{error}</Alert>}

      <div className="deliveries-table-wrap">
        {loading ? (
          <div className="text-center py-5 deliveries-spinner-wrap">
            <Spinner animation="border" />
            <p className="text-muted small mt-2 mb-0">Loading deliveries…</p>
          </div>
        ) : (
          <div className="table-responsive">
            <Table hover className="mb-0 align-middle">
              <thead>
                <tr>
                  <th>Proj</th>
                  <th>BOL</th>
                  <th>Load #</th>
                  <th>Status</th>
                  <th>Project</th>
                  <th>Sched. delivery</th>
                  <th>Last event</th>
                  <th
                    className="text-nowrap text-center"
                    title="Report issue / notes from the driver app (delay, breakdown, etc.)"
                  >
                    Driver notes
                  </th>
                  <th className="text-center">Alerts</th>
                  <th className="text-end">Actions</th>
                </tr>
              </thead>
              <tbody>
                {rows.length === 0 ? (
                  <tr>
                    <td colSpan={10} className="text-center text-muted py-5">
                      No rows match your filters. Issue a driver token and complete a scan to see activity.
                    </td>
                  </tr>
                ) : (
                  rows.map((r) => (
                    <tr key={r.loadId}>
                      <td className="small text-muted text-nowrap">{r.projectId ?? '—'}</td>
                      <td className="fw-semibold text-dark">{r.bolNo || '—'}</td>
                      <td>{r.loadNo || '—'}</td>
                      <td>
                        <Badge {...deliveriesLoadStatusBadgeProps(r.status)}>
                          {loadStatusDisplayFromRaw(r.status)}
                        </Badge>
                      </td>
                      <td>
                        <div className="small text-truncate" style={{ maxWidth: 220 }} title={r.custProjName || r.projectName}>
                          {r.custProjName || r.projectName || '—'}
                        </div>
                      </td>
                      <td className="small text-nowrap text-muted">
                        {formatDateOnly(r.scheduledDeliveryDate)}
                      </td>
                      <td className="small text-nowrap text-muted">
                        <div>{formatDt(r.LastOccurredAt)}</div>
                        {r.LastEventType ? (
                          <div className="text-muted" style={{ fontSize: '0.75rem' }}>
                            {humanizeEventType(r.LastEventType)}
                          </div>
                        ) : null}
                      </td>
                      <td className="small text-center">
                        {Number(r.driverNoteCount) > 0 ? (
                          <Badge bg="info" text="dark" className="fw-normal">
                            {Number(r.driverNoteCount)}
                          </Badge>
                        ) : (
                          <span className="text-muted">—</span>
                        )}
                      </td>
                      <td className="text-center">
                        {Number(r.openAlertsCount) > 0 ? (
                          <Badge bg="danger">{r.openAlertsCount}</Badge>
                        ) : (
                          <span className="text-muted small">0</span>
                        )}
                      </td>
                      <td className="text-end">
                        <Button
                          size="sm"
                          variant="outline-primary"
                          className="deliveries-btn--outline-primary"
                          onClick={() => openDetail(r.loadId)}
                        >
                          View
                        </Button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </Table>
          </div>
        )}
      </div>

      {!loading && pagination.total > 0 && (
        <div className="deliveries-pagination-bar">
          <div className="deliveries-pagination-bar__meta">
            <span className="muted">
              Showing{' '}
              {Math.min((pagination.page - 1) * (pagination.limit || pageLimit) + 1, pagination.total)}–
              {Math.min(pagination.page * (pagination.limit || pageLimit), pagination.total)} of{' '}
              {pagination.total} deliveries
              {pagination.totalPages > 1 ? (
                <>
                  {' '}
                  · page {pagination.page} of {pagination.totalPages}
                </>
              ) : null}
            </span>
            <div className="deliveries-pagination-bar__perpage">
              <Form.Label htmlFor="deliveries-page-size" className="muted mb-0 small text-nowrap">
                Per page
              </Form.Label>
              <Form.Select
                id="deliveries-page-size"
                size="sm"
                className="deliveries-pagination-bar__select"
                value={String(pageLimit)}
                disabled={loading}
                onChange={(e) => {
                  const next = Number(e.target.value);
                  setPageLimit(next);
                  loadDeliveries(1, { limit: next });
                }}
                aria-label="Deliveries rows per page"
              >
                {DELIVERY_PAGE_LIMIT_OPTIONS.map((n) => (
                  <option key={n} value={String(n)}>
                    {n}
                  </option>
                ))}
              </Form.Select>
            </div>
          </div>
          <Pagination className="mb-0 flex-wrap">
            <Pagination.Prev
              disabled={pagination.page <= 1 || loading}
              onClick={() => loadDeliveries(pagination.page - 1)}
              aria-label="Previous page"
            />
            {visiblePageNumbers(pagination.page, pagination.totalPages).map((item, idx) =>
              item === 'ellipsis' ? (
                <Pagination.Ellipsis key={`de-pe-${idx}`} disabled />
              ) : (
                <Pagination.Item
                  key={item}
                  active={item === pagination.page}
                  onClick={() => (item !== pagination.page ? loadDeliveries(item) : undefined)}
                >
                  {item}
                </Pagination.Item>
              )
            )}
            <Pagination.Next
              disabled={pagination.page >= pagination.totalPages || loading}
              onClick={() => loadDeliveries(pagination.page + 1)}
              aria-label="Next page"
            />
          </Pagination>
        </div>
      )}

      <Modal
        show={detailShow}
        onHide={() => setDetailShow(false)}
        size="xl"
        scrollable
        centered
        className="deliveries-detail-modal"
      >
        <Modal.Header closeButton>
          <div>
            <Modal.Title as="div">
              {load ? (
                <>
                  <span className="d-flex align-items-center gap-2">
                    <Package size={22} />
                    Load {load.loadNo}
                  </span>
                  <div className="modal-subtitle">
                    BOL {load.bolNo || '—'}
                    {(load.custProjName || load.projectName) && (
                      <>
                        {' · '}
                        {load.custProjName || load.projectName}
                      </>
                    )}
                  </div>
                </>
              ) : (
                'Delivery detail'
              )}
            </Modal.Title>
          </div>
        </Modal.Header>
        <Modal.Body>
          {detailLoading && (
            <div className="text-center py-5 deliveries-spinner-wrap">
              <Spinner animation="border" />
            </div>
          )}
          {!detailLoading && detail?.error && (
            <div className="p-3">
              <Alert variant="danger">{detail.error}</Alert>
            </div>
          )}
          {!detailLoading && load && (
            <>
              <div className="deliveries-detail-section">
                <div className="deliveries-detail-section__label">Load &amp; project</div>
                <dl className="deliveries-detail-grid m-0">
                  <div>
                    <dt>Status</dt>
                    <dd>
                      <Badge {...deliveriesLoadStatusBadgeProps(load.status)}>
                        {loadStatusDisplayFromRaw(load.status)}
                      </Badge>
                    </dd>
                  </div>
                  <div>
                    <dt>Scheduled delivery</dt>
                    <dd>{formatScheduledDeliveryField(load.deliveryDate)}</dd>
                  </div>
                  <div>
                    <dt>Fabrication</dt>
                    <dd>{load.fabricationAlias ?? load.fabricationLocation ?? '—'}</dd>
                  </div>
                  <div>
                    <dt>Delivery loc. ID</dt>
                    <dd>{load.deliveryLocation ?? '—'}</dd>
                  </div>
                  {displayAddressHeadingLine && (
                    <div style={{ gridColumn: '1 / -1' }}>
                      <dt>{bolStyleDropSiteLine ? 'Drop site' : 'Project address'}</dt>
                      <dd className="fw-normal">
                        <div className="mb-1">{displayAddressHeadingLine}</div>
                      </dd>
                    </div>
                  )}
                  {showDetailMap ? (
                    <div
                      id="deliveries-detail-pickup-map"
                      ref={detailMapRef}
                      className="deliveries-detail-map-anchor"
                      style={{ gridColumn: '1 / -1' }}
                    >
                      <dt>Pickup &amp; delivery map</dt>
                      <dd className="mb-0">
                        <DeliveriesRouteMap
                          show={detailShow}
                          pickupPins={routeMap?.pickup || []}
                          deliveryPin={routeMap?.delivery}
                          actualPickupPin={actualPickupPin}
                          actualDeliveryPin={actualDeliveryPin}
                        />
                        {(routeMap?.delivery && googleMapsUrl(routeMap.delivery.lat, routeMap.delivery.lng)) ||
                        (actualDeliveryPin && googleMapsUrl(actualDeliveryPin.lat, actualDeliveryPin.lng)) ||
                        (!routeMap?.delivery && googleMapsUrl(load.projectMapLat, load.projectMapLng)) ? (
                          <div className="deliveries-map-quicklinks">
                            <span className="deliveries-map-quicklinks__label small text-muted">Quick open</span>
                            <div className="deliveries-map-quicklinks__buttons">
                              {routeMap?.delivery && googleMapsUrl(routeMap.delivery.lat, routeMap.delivery.lng) && (
                                <Button
                                  as="a"
                                  href={googleMapsUrl(routeMap.delivery.lat, routeMap.delivery.lng)}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  variant="outline-secondary"
                                  size="sm"
                                  className="deliveries-map-quicklinks__btn"
                                >
                                  <ExternalLink size={14} className="me-1" aria-hidden />
                                  Planned delivery
                                </Button>
                              )}
                              {actualDeliveryPin && googleMapsUrl(actualDeliveryPin.lat, actualDeliveryPin.lng) && (
                                <Button
                                  as="a"
                                  href={googleMapsUrl(actualDeliveryPin.lat, actualDeliveryPin.lng)}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  variant="outline-secondary"
                                  size="sm"
                                  className="deliveries-map-quicklinks__btn"
                                >
                                  <ExternalLink size={14} className="me-1" aria-hidden />
                                  Actual dropoff
                                </Button>
                              )}
                              {!routeMap?.delivery && googleMapsUrl(load.projectMapLat, load.projectMapLng) && (
                                <Button
                                  as="a"
                                  href={googleMapsUrl(load.projectMapLat, load.projectMapLng)}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  variant="outline-secondary"
                                  size="sm"
                                  className="deliveries-map-quicklinks__btn"
                                >
                                  <ExternalLink size={14} className="me-1" aria-hidden />
                                  Project location
                                </Button>
                              )}
                            </div>
                          </div>
                        ) : null}
                      </dd>
                    </div>
                  ) : (
                    googleMapsUrl(load.projectMapLat, load.projectMapLng) && (
                      <div style={{ gridColumn: '1 / -1' }}>
                        <dt>Map</dt>
                        <dd className="mb-0">
                          <a
                            href={googleMapsUrl(load.projectMapLat, load.projectMapLng)}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="d-inline-flex align-items-center gap-1"
                          >
                            <ExternalLink size={14} />
                            Open in Google Maps
                          </a>
                        </dd>
                      </div>
                    )
                  )}
                </dl>
              </div>

              {detail.timing && (
                <div className="deliveries-detail-section">
                  <div className="deliveries-detail-section__label d-flex align-items-center gap-2">
                    <Timer size={16} />
                    Delivery timing
                  </div>
                  <p className="deliveries-section-subtext small text-muted mb-2 px-1">
                    <strong>Scheduled</strong> uses the date and time stored on the load. If the
                    stored time is midnight (date-only), the on-time deadline is end of that calendar day.{' '}
                    <strong>Site arrival</strong> is the driver’s GPS check at the drop; <strong>completed</strong> is
                    proof of delivery. <strong>Unloading</strong> is the time between those two.
                  </p>
                  <dl className="deliveries-detail-grid m-0">
                    <div>
                      <dt>Scheduled</dt>
                      <dd>{formatDt(detail.timing.scheduledDeliveryAt)}</dd>
                    </div>
                    <div>
                      <dt>Site arrival</dt>
                      <dd>{formatDt(detail.timing.siteArrivalAt)}</dd>
                    </div>
                    <div>
                      <dt>Completed</dt>
                      <dd>{formatDt(detail.timing.deliveryCompletedAt)}</dd>
                    </div>
                    <div>
                      <dt>Unloading</dt>
                      <dd>
                        {formatUnloadDuration(detail.timing.unloadingMinutes)}
                      </dd>
                    </div>
                    <div>
                      <dt>Arrival vs schedule</dt>
                      <dd className="d-flex flex-wrap align-items-center gap-2">
                        <Badge bg={punctualityBadgeBg(detail.timing.arrivalVsSchedule)}>
                          {punctualityLabel(detail.timing.arrivalVsSchedule)}
                        </Badge>
                        {detail.timing.minutesLateArrival != null &&
                          detail.timing.arrivalVsSchedule === 'late' && (
                            <span className="small text-muted">
                              ({detail.timing.minutesLateArrival} min after deadline)
                            </span>
                          )}
                      </dd>
                    </div>
                    <div>
                      <dt>Completion vs schedule</dt>
                      <dd className="d-flex flex-wrap align-items-center gap-2">
                        <Badge bg={punctualityBadgeBg(detail.timing.completionVsSchedule)}>
                          {punctualityLabel(detail.timing.completionVsSchedule)}
                        </Badge>
                        {detail.timing.minutesLateCompletion != null &&
                          detail.timing.completionVsSchedule === 'late' && (
                            <span className="small text-muted">
                              ({detail.timing.minutesLateCompletion} min after deadline)
                            </span>
                          )}
                      </dd>
                    </div>
                  </dl>
                </div>
              )}

              {detail.latestToken && (
                <div className="deliveries-detail-section">
                  <div className="deliveries-detail-section__label d-flex align-items-center gap-2">
                    <Smartphone size={16} aria-hidden />
                    Driver mobile link
                  </div>
                  <p className="deliveries-section-subtext small text-muted mb-2 px-1">
                    QR code for this load in the driver app. Only the <strong>active</strong> link works; reissuing a QR
                    turns older links off.
                  </p>
                  <div className="deliveries-detail-token deliveries-detail-token--friendly mx-0">
                    <div className="deliveries-detail-token__row">
                      <div>
                        <div className="deliveries-detail-token__eyebrow">Link status</div>
                        <div className="deliveries-detail-token__status">
                          {detail.latestToken.IsActive === 1 || detail.latestToken.isActive === 1 ? (
                            <>
                              <Badge bg="success">Active</Badge>
                              <span className="deliveries-detail-token__status-note text-muted">
                                Use this QR for drivers.
                              </span>
                            </>
                          ) : (
                            <>
                              <Badge bg="secondary">Inactive</Badge>
                              <span className="deliveries-detail-token__status-note text-muted">
                                Superseded by a newer link — do not share this QR.
                              </span>
                            </>
                          )}
                        </div>
                      </div>
                      <div className="deliveries-detail-token__version text-md-end">
                        <div className="deliveries-detail-token__eyebrow">Version</div>
                        <div className="fw-semibold">
                          {detail.latestToken.TokenVersion ?? detail.latestToken.tokenVersion ?? '—'}
                        </div>
                      </div>
                    </div>
                    <dl className="deliveries-detail-token__meta">
                      <div>
                        <dt>Issued</dt>
                        <dd>{formatDt(detail.latestToken.IssuedAt ?? detail.latestToken.issuedAt)}</dd>
                      </div>
                      <div>
                        <dt>Expires</dt>
                        <dd>{formatDt(detail.latestToken.ExpiresAt ?? detail.latestToken.expiresAt)}</dd>
                      </div>
                      {(detail.latestToken.ExternalRef || detail.latestToken.externalRef) && (
                        <div style={{ gridColumn: '1 / -1' }}>
                          <dt>Reference</dt>
                          <dd className="text-break">
                            {detail.latestToken.ExternalRef ?? detail.latestToken.externalRef}
                          </dd>
                        </div>
                      )}
                    </dl>
                  </div>
                </div>
              )}

              <div className="deliveries-detail-section">
                <div className="deliveries-detail-section__label d-flex align-items-center gap-2">
                  <ShieldAlert size={16} />
                  Alerts
                </div>
                <p className="deliveries-section-subtext small text-muted mb-2 px-1">
                  Warnings when GPS or checks don’t match the plan. <strong>Medium</strong> or higher usually needs a
                  look; <strong>Resolved</strong> stays on the record for audit.
                </p>
                {(detail.alerts || []).length === 0 ? (
                  <p className="text-muted small mb-0 px-1">No alerts for this load.</p>
                ) : (
                  (detail.alerts || []).map((a) => (
                    <div
                      key={a.id}
                      className={`deliveries-alert-card ${a.isResolved ? 'is-resolved' : ''}`}
                    >
                      <div className="deliveries-alert-card__top">
                        <div className="flex-grow-1 min-w-0">
                          <div className="deliveries-alert-card__title">
                            <strong className="deliveries-alert-card__headline">{humanizeAlertType(a.alertType)}</strong>
                            {a.severity && (
                              <Badge
                                bg={severityVariant(a.severity)}
                                text={severityVariant(a.severity) === 'warning' ? 'dark' : undefined}
                                className="ms-2 deliveries-alert-card__severity"
                              >
                                {formatSeverityLabel(a.severity)}
                              </Badge>
                            )}
                            {a.isResolved && (
                              <Badge bg="secondary" className="ms-2 text-uppercase small">
                                Resolved
                              </Badge>
                            )}
                          </div>
                          {alertTypeHint(a.alertType) && (
                            <div className="deliveries-alert-card__hint small">{alertTypeHint(a.alertType)}</div>
                          )}
                        </div>
                        {!a.isResolved &&
                          (a.alertType === SLA_ALERT_TYPE ? (
                            <Button
                              size="sm"
                              variant="success"
                              className="deliveries-btn--success flex-shrink-0"
                              onClick={() => {
                                setSlaModalNotes('');
                                setSlaResolveModal({ alertId: a.id, loadId: load.loadId });
                              }}
                            >
                              Resolve…
                            </Button>
                          ) : (
                            <Button
                              size="sm"
                              variant="success"
                              className="deliveries-btn--success flex-shrink-0"
                              onClick={() => resolveAlert(a.id)}
                            >
                              Resolve
                            </Button>
                          ))}
                      </div>
                      <div className="deliveries-alert-card__times small text-muted mt-2">
                        <Clock size={12} className="me-1 flex-shrink-0" />
                        <span>
                          Raised <strong className="fw-semibold text-body-secondary">{formatDt(a.createdAt)}</strong>
                          {a.isResolved && a.resolvedAt && (
                            <>
                              {' '}
                              · Cleared <strong className="fw-semibold text-body-secondary">{formatDt(a.resolvedAt)}</strong>
                            </>
                          )}
                        </span>
                      </div>
                      {a.notes && (
                        <div className="deliveries-alert-card__notes small mt-2">
                          <span className="text-muted">Note: </span>
                          {a.notes}
                        </div>
                      )}
                    </div>
                  ))
                )}
              </div>

              <div className="deliveries-detail-section">
                <div className="deliveries-detail-section__label">Event timeline</div>
                {(detail.events || []).length > 0 && (
                  <p className="deliveries-timeline__hint small text-muted mb-2 px-1">
                    <strong>Newest first.</strong> Quick app opens that didn’t record GPS are grouped into a single line so
                    the important pickup and delivery steps stay visible.
                  </p>
                )}
                <div className="deliveries-timeline">
                  {(detail.events || []).length === 0 ? (
                    <p className="text-muted small mb-0 p-3">No events logged.</p>
                  ) : (
                    timelineRows.map((row) => {
                      if (row.kind === 'group') {
                        return (
                          <div key={row.key} className="deliveries-timeline__item deliveries-timeline__item--group">
                            <div className="deliveries-timeline__time">{formatDt(row.newestAt)}</div>
                            <div>
                              <div className="d-flex flex-wrap align-items-center gap-2">
                                <div className="deliveries-timeline__event mb-0">QR validated</div>
                                <Badge bg="light" text="dark" className="deliveries-timeline__group-badge">
                                  Grouped · no GPS
                                </Badge>
                              </div>
                              <div className="deliveries-timeline__delta mt-1">
                                {row.count} token or app check{row.count !== 1 ? 's' : ''} (location not recorded on these rows)
                              </div>
                              {row.count > 1 && (
                                <div className="deliveries-timeline__meta small text-muted mt-1">
                                  Newest in group: {formatDt(row.newestAt)} · Oldest: {formatDt(row.oldestAt)}
                                </div>
                              )}
                            </div>
                            <div className="deliveries-timeline__links">
                              <span className="text-muted small">—</span>
                            </div>
                          </div>
                        );
                      }
                      const ev = row.event;
                      const actUrl = googleMapsUrl(ev.latitude, ev.longitude);
                      const expUrl = googleMapsUrl(ev.expectedLatitude, ev.expectedLongitude);
                      const hasGpsActual =
                        ev.latitude != null &&
                        ev.longitude != null &&
                        !Number.isNaN(Number(ev.latitude)) &&
                        !Number.isNaN(Number(ev.longitude));
                      const hasGpsExpected =
                        ev.expectedLatitude != null &&
                        ev.expectedLongitude != null &&
                        !Number.isNaN(Number(ev.expectedLatitude)) &&
                        !Number.isNaN(Number(ev.expectedLongitude));
                      const inside =
                        ev.insideGeofence === true || ev.insideGeofence === 1
                          ? 'inside'
                          : ev.insideGeofence === false || ev.insideGeofence === 0
                            ? 'outside'
                            : null;
                      const distLabel = formatDistanceMeters(ev.distanceMeters);
                      const radiusLabel =
                        ev.radiusMeters != null && Number.isFinite(Number(ev.radiusMeters))
                          ? `${Math.round(Number(ev.radiusMeters))} m`
                          : null;
                      const isDriverNote = ev.eventType === 'driver_note';
                      return (
                        <div
                          key={row.key}
                          className={`deliveries-timeline__item${isDriverNote ? ' deliveries-timeline__item--driver-note' : ''}`}
                        >
                          <div className="deliveries-timeline__time">{formatDt(ev.occurredAt)}</div>
                          <div>
                            <div className="deliveries-timeline__event d-flex flex-wrap align-items-center gap-2">
                              {humanizeEventType(ev.eventType)}
                              {isDriverNote ? (
                                <span className="deliveries-timeline__driver-note-pill">Driver report</span>
                              ) : null}
                            </div>
                            <div className="deliveries-timeline__delta">
                              {statusDeltaLine(ev.statusBefore, ev.statusAfter)}
                            </div>
                            <div className="deliveries-timeline__geo mt-2">
                              {hasGpsActual && (
                                <div className="deliveries-timeline__geoChips">
                                  {distLabel && (
                                    <span className="deliveries-chip deliveries-chip--neutral">
                                      {distLabel} from expected
                                    </span>
                                  )}
                                  {radiusLabel && (
                                    <span className="deliveries-chip deliveries-chip--neutral">
                                      Zone r {radiusLabel}
                                    </span>
                                  )}
                                  {ev.overrideUsed ? (
                                    <span className="deliveries-chip deliveries-chip--warning">Override</span>
                                  ) : null}
                                  {inside === 'inside' && (
                                    <span className="deliveries-chip deliveries-chip--success">In zone</span>
                                  )}
                                  {inside === 'outside' && (
                                    <span className="deliveries-chip deliveries-chip--danger">Out of zone</span>
                                  )}
                                </div>
                              )}
                              {(hasGpsActual || hasGpsExpected) && !showDetailMap && (
                                <div className="deliveries-timeline__geo-note">
                                  Open Google Maps below for coordinates (map is unavailable for this load).
                                </div>
                              )}
                              {hasGpsActual && ev.metadata?.gpsAccuracyMeters != null && (
                                <span className="deliveries-chip deliveries-chip--neutral">
                                  ±{Math.round(ev.metadata.gpsAccuracyMeters)} m accuracy
                                </span>
                              )}
                            </div>
                            {ev.overrideReason && (
                              <div className="small text-muted mt-1 fst-italic">{ev.overrideReason}</div>
                            )}
                            {driverNoteBlock(ev)}
                            {proofOfDeliveryBlock(ev)}
                            {adminManualDeliveredBlock(ev)}
                          </div>
                          <div className="deliveries-timeline__map-actions">
                            {(actUrl || expUrl) && showDetailMap && (
                              <Button
                                type="button"
                                variant="outline-secondary"
                                size="sm"
                                className="deliveries-timeline__map-btn"
                                onClick={scrollToDetailMap}
                              >
                                <MapPin size={14} />
                                View on map
                              </Button>
                            )}
                            {actUrl && (
                              <Button
                                as="a"
                                variant="outline-primary"
                                size="sm"
                                className="deliveries-timeline__map-btn"
                                href={actUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                              >
                                <ExternalLink size={14} />
                                Actual in Google Maps
                              </Button>
                            )}
                            {expUrl && (
                              <Button
                                as="a"
                                variant="outline-primary"
                                size="sm"
                                className="deliveries-timeline__map-btn"
                                href={expUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                              >
                                <ExternalLink size={14} />
                                Expected in Google Maps
                              </Button>
                            )}
                            {!actUrl && !expUrl && <span className="text-muted small">—</span>}
                          </div>
                        </div>
                      );
                    })
                  )}
                </div>
                {hasAdminManualDelivered && (
                  <p className="deliveries-timeline__manual-audit small text-muted mt-2 mb-0 px-1">
                    This load has a <strong>manual delivery completion</strong> recorded from Streamline (admin), not only
                    driver-app scans.
                  </p>
                )}
              </div>
            </>
          )}
        </Modal.Body>
      </Modal>

      <Modal show={!!slaResolveModal} onHide={closeSlaResolveModal} centered className="deliveries-sla-resolve-modal">
        <Modal.Header closeButton>
          <Modal.Title as="div" className="h6">
            Past delivery window alert
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="small text-muted mb-3">
            Clear the alert only, or mark the load <strong>Delivered</strong> in Streamline if you confirmed delivery
            outside the driver workflow. Optional note is saved on the alert resolve and on the timeline when you mark
            delivered.
          </p>
          <Form.Group className="mb-0" controlId="sla-resolve-notes">
            <Form.Label className="small text-muted">Note (optional)</Form.Label>
            <Form.Control
              as="textarea"
              rows={3}
              value={slaModalNotes}
              onChange={(e) => setSlaModalNotes(e.target.value)}
              disabled={slaModalSubmitting}
              placeholder="e.g. Confirmed by site call, POD emailed…"
            />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer className="flex-wrap gap-2">
          <Button variant="outline-secondary" onClick={closeSlaResolveModal} disabled={slaModalSubmitting}>
            Cancel
          </Button>
          <Button variant="outline-primary" onClick={submitSlaResolveAlertOnly} disabled={slaModalSubmitting}>
            {slaModalSubmitting ? '…' : 'Resolve alert only'}
          </Button>
          <Button variant="success" onClick={submitSlaManualDelivered} disabled={slaModalSubmitting}>
            {slaModalSubmitting ? '…' : 'Mark delivered in Streamline'}
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal
        show={perfDetailShow}
        onHide={closePerfRowDetail}
        size="lg"
        centered
        className="deliveries-perf-detail-modal"
      >
        <Modal.Header closeButton className="deliveries-perf-detail-modal__header">
          <div>
            <Modal.Title as="div" className="h5 mb-0 d-flex align-items-center gap-2">
              <Timer size={22} className="text-body-secondary" aria-hidden />
              Performance · Load {perfDetailRow?.loadNo ?? '—'}
            </Modal.Title>
            <div className="small text-muted mt-1">
              BOL {perfDetailRow?.bolNo || '—'} · Project ID {perfDetailRow?.projectId ?? '—'}
              {(perfDetailRow?.custProjName || perfDetailRow?.projectName) && (
                <>
                  <br />
                  <span className="text-body">{perfDetailRow?.custProjName || perfDetailRow?.projectName}</span>
                </>
              )}
            </div>
          </div>
        </Modal.Header>
        <Modal.Body className="pt-0">
          <Alert variant="light" className="deliveries-perf-detail-modal__explain border small mb-3">
            <strong className="d-block mb-1">How to read this</strong>
            Scheduled delivery is the deadline from the load. <strong>Site arrival</strong> is when the driver checked in
            at the drop; <strong>Handoff</strong> is when delivery was confirmed. Unload time is the minutes between those
            two. On-time / late compares each time to the deadline.
          </Alert>
          {perfDetailRow && (
            <>
              <dl className="deliveries-detail-grid m-0 mb-3">
                <div>
                  <dt>Scheduled delivery</dt>
                  <dd>{formatDt(perfDetailRow.scheduledDeliveryAt)}</dd>
                </div>
                <div>
                  <dt>Site arrival</dt>
                  <dd>{formatDt(perfDetailRow.siteArrivalAt)}</dd>
                </div>
                <div>
                  <dt>Handoff completed</dt>
                  <dd>{formatDt(perfDetailRow.deliveryCompletedAt)}</dd>
                </div>
                <div>
                  <dt>Unload time</dt>
                  <dd>
                    {formatUnloadDuration(perfDetailRow.unloadingMinutes)}
                  </dd>
                </div>
                <div>
                  <dt>Arrival vs schedule</dt>
                  <dd className="d-flex flex-wrap align-items-center gap-2">
                    <Badge bg={punctualityBadgeBg(perfDetailRow.arrivalVsSchedule)}>
                      {punctualityLabel(perfDetailRow.arrivalVsSchedule)}
                    </Badge>
                    {perfDetailRow.minutesLateArrival != null && perfDetailRow.arrivalVsSchedule === 'late' && (
                      <span className="small text-muted">({perfDetailRow.minutesLateArrival} min after deadline)</span>
                    )}
                  </dd>
                </div>
                <div>
                  <dt>Handoff vs schedule</dt>
                  <dd className="d-flex flex-wrap align-items-center gap-2">
                    <Badge bg={punctualityBadgeBg(perfDetailRow.completionVsSchedule)}>
                      {punctualityLabel(perfDetailRow.completionVsSchedule)}
                    </Badge>
                    {perfDetailRow.minutesLateCompletion != null && perfDetailRow.completionVsSchedule === 'late' && (
                      <span className="small text-muted">({perfDetailRow.minutesLateCompletion} min after deadline)</span>
                    )}
                  </dd>
                </div>
              </dl>
              <div className="d-flex flex-wrap gap-2 justify-content-end">
                <Button variant="outline-secondary" size="sm" onClick={closePerfRowDetail}>
                  Close
                </Button>
                <Button variant="primary" size="sm" onClick={openFullDeliveryFromPerf}>
                  <Package size={14} className="me-1" aria-hidden />
                  Open full delivery
                </Button>
              </div>
            </>
          )}
        </Modal.Body>
      </Modal>
    </Container>
  );
};

export default Deliveries;
